import React, { useState, useEffect } from "react";
import { HiPlus, HiOutlineTrash } from "react-icons/hi";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import { buildApiUrl } from "../../../config";

const LocalPurchaseOrderEditModal = ({
  isOpen,
  closeModal,
  orderId,
  fetchLocalPurchaseOrders,
}) => {
  const [orderData, setOrderData] = useState(null);
  const [updatedItems, setUpdatedItems] = useState([]);
  const [status, setStatus] = useState("");
  const [warehouseName, setWarehouseName] = useState("");
  const [purchaseNo, setPurchaseNo] = useState("");

  useEffect(() => {
    if (orderId) {
      fetchOrderData(orderId);
    }
  }, [orderId]);

  const fetchOrderData = async (orderId) => {
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        buildApiUrl(`/api/localpurchaseorder/${orderId}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data) {
        setOrderData(data);
        setUpdatedItems(data.items || []);
        setStatus(data.status || "");
        setWarehouseName(data.warehouseName);
        setPurchaseNo(data.purchaseNo);
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch order details.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem("token");

    const updatedOrder = {
      purchaseNo: purchaseNo,
      warehouseName: warehouseName,
      items: updatedItems,
      status: status,
    };

    try {
      const response = await fetch(
        buildApiUrl(`/api/localpurchaseorder/${orderId}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedOrder),
        }
      );

      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "The order has been updated.",
          icon: "success",
          confirmButtonColor: "#4CAF50",
        });
        fetchLocalPurchaseOrders();
        closeModal();
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to update the order.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "An error occurred while updating the order.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleAddItem = () => {
    setUpdatedItems([
      ...updatedItems,
      {
        itemCategory: "",
        itemName: "",
        itemImage: "",
        itemCode: "",
        pcsPerCtn: 0,
        itemQuantity: 0,
        itemQuantityCartons: 0,
        itemPrice: 0,
      },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItemsList = [...updatedItems];
    updatedItemsList[index][field] = value;
    setUpdatedItems(updatedItemsList);
  };

  const handleRemoveItem = (index) => {
    const updatedItemsList = [...updatedItems];
    updatedItemsList.splice(index, 1);
    setUpdatedItems(updatedItemsList);
  };

  const handleClose = () => {
    setOrderData(null);
    closeModal();
  };

  return isOpen && orderData ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div
        className="w-full max-w-6xl rounded-lg bg-white shadow-xl overflow-y-auto"
        style={{ maxHeight: "90vh", scrollbarWidth: "thin" }}
      >
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Local Purchase Order
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Purchase No
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={purchaseNo}
                onChange={(e) => setPurchaseNo(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Warehouse
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={warehouseName}
                onChange={(e) => setWarehouseName(e.target.value)}
              />
            </div> */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Items</h3>
            <div className="hidden sm:grid grid-cols-[1fr_1fr_80px_80px_80px_20px] gap-4 text-xs font-medium tracking-wide py-2 text-gray-700 bg-gray-200 rounded-md mb-2 items-center">
              <span className="pl-2">Category</span>
              <span>Item Name</span>
              <span>Quantity</span>
              <span>Cartons</span>
              <span>Price</span>
              <span></span>
            </div>
            {updatedItems.map((item, index) => (
              <div
                key={index}
                className="mb-2 rounded-md border sm:border-0 border-gray-200 bg-gray-50 p-3 sm:p-0"
              >
                <div className="grid grid-cols-1 gap-4 items-center sm:grid-cols-[1fr_1fr_80px_80px_80px_20px]">
                  <div>
                    <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                      Category
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={item.itemCategory}
                      onChange={(e) =>
                        handleItemChange(index, "itemCategory", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                      Item Name
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={item.itemName}
                      onChange={(e) =>
                        handleItemChange(index, "itemName", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                      Quantity
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={item.itemQuantity}
                      onChange={(e) =>
                        handleItemChange(index, "itemQuantity", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                      Cartons
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={item.itemQuantityCartons}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "itemQuantityCartons",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                      Price
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={item.itemPrice}
                      onChange={(e) =>
                        handleItemChange(index, "itemPrice", e.target.value)
                      }
                    />
                  </div>
                  {updatedItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="flex items-center justify-center gap-1 text-xs text-red-600 hover:text-red-800"
                    >
                      <HiOutlineTrash size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleAddItem}
              className="flex items-center justify-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              <HiPlus size={16} />
              Add Item
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default LocalPurchaseOrderEditModal;
